import React from "react";
import { useRoutes, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import LoginPage from "../login/page";
import DashboardPage from "../dashboard/page";
import UsersPage from "../users/page";
import EditUserPage from "../users/edit/[id]";
import ViewUserPage from "../users/view/[id]";
import NewUserPage from "../users/new/NewUserPage";
import ProfilePage from "../profile/ProfilePage";
import OrganizationsPage from "../organizations/page";
import NewOrganizationPage from "../organizations/new/NewOrganizationPage";
import ViewOrganizationPage from "../organizations/view/[id]";
import EditOrganizationPage from "../organizations/edit/[id]";
import AppsPage from "../apps/page";
import EditAppPage from "../apps/edit/[id]";
import ViewAppPage from "../apps/view/[id]";
import NewAppPage from "../apps/new/page";
import RolesPage from "../roles/page";
import NewOrgRolePage from "../roles/new/NewOrgRolePage";
import NewUserTypePage from "../roles/new/NewUserTypePage";
import NewAppAccessLevelPage from "../roles/new/NewAppAccessLevelPage";
import EditOrgRolePage from "../roles/edit/EditOrgRolePage";
import EditUserTypePage from "../roles/edit/EditUserTypePage";
import EditAppAccessLevelPage from "../roles/edit/EditAppAccessLevelPage";
import ViewOrgRolePage from "../roles/view/ViewOrgRolePage";
import ViewUserTypePage from "../roles/view/ViewUserTypePage";
import ViewAppAccessLevelPage from "../roles/view/ViewAppAccessLevelPage";
import ConnectionsPage from "../connections/page";
import NewOrgConnectionPage from "../connections/new/NewOrgConnectionPage";
import NewUserConnectionPage from "../connections/new/NewUserConnectionPage";

const ProtectedWrapper = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

const PublicWrapper = () => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Outlet />;
};

const AppRoutes = () => {
  const routes = useRoutes([
    {
      path: "/",
      element: <PublicWrapper />,
      children: [
        { path: "", element: <LoginPage /> },
        { path: "login", element: <LoginPage /> },
      ],
    },
    {
      path: "/",
      element: <ProtectedWrapper />,
      children: [
        { path: "dashboard", element: <DashboardPage /> },
        {
          path: "users",
          element: (
            <DashboardPage>
              <UsersPage />
            </DashboardPage>
          ),
        },
        {
          path: "users/edit/:id",
          element: (
            <DashboardPage>
              <EditUserPage />
            </DashboardPage>
          ),
        },
        {
          path: "users/view/:id",
          element: (
            <DashboardPage>
              <ViewUserPage />
            </DashboardPage>
          ),
        },
        {
          path: "users/new",
          element: (
            <DashboardPage>
              <NewUserPage />
            </DashboardPage>
          ),
        },
        {
          path: "profile",
          element: (
            <DashboardPage>
              <ProfilePage />
            </DashboardPage>
          ),
        },
        {
          path: "organizations",
          element: (
            <DashboardPage>
              <OrganizationsPage />
            </DashboardPage>
          ),
        },
        {
          path: "organizations/new",
          element: (
            <DashboardPage>
              <NewOrganizationPage />
            </DashboardPage>
          ),
        },
        {
          path: "organizations/view/:id",
          element: (
            <DashboardPage>
              <ViewOrganizationPage />
            </DashboardPage>
          ),
        },
        {
          path: "organizations/edit/:id",
          element: (
            <DashboardPage>
              <EditOrganizationPage />
            </DashboardPage>
          ),
        },
        {
          path: "apps",
          element: (
            <DashboardPage>
              <AppsPage />
            </DashboardPage>
          ),
        },
        {
          path: "apps/view/:id",
          element: (
            <DashboardPage>
              <ViewAppPage />
            </DashboardPage>
          ),
        },
        {
          path: "apps/edit/:id",
          element: (
            <DashboardPage>
              <EditAppPage />
            </DashboardPage>
          ),
        },
        {
          path: "apps/new",
          element: (
            <DashboardPage>
              <NewAppPage />
            </DashboardPage>
          ),
        },
        {
          path: "roles",
          element: (
            <DashboardPage>
              <RolesPage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/new-org-role",
          element: (
            <DashboardPage>
              <NewOrgRolePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/new-user-type",
          element: (
            <DashboardPage>
              <NewUserTypePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/new-app-access",
          element: (
            <DashboardPage>
              <NewAppAccessLevelPage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/edit-org-role/:id",
          element: (
            <DashboardPage>
              <EditOrgRolePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/edit-user-type/:id",
          element: (
            <DashboardPage>
              <EditUserTypePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/edit-app-access/:id",
          element: (
            <DashboardPage>
              <EditAppAccessLevelPage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/view-org-role/:id",
          element: (
            <DashboardPage>
              <ViewOrgRolePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/view-user-type/:id",
          element: (
            <DashboardPage>
              <ViewUserTypePage />
            </DashboardPage>
          ),
        },
        {
          path: "roles/view-app-access/:id",
          element: (
            <DashboardPage>
              <ViewAppAccessLevelPage />
            </DashboardPage>
          ),
        },
        {
          path: "connections",
          element: (
            <DashboardPage>
              <ConnectionsPage />
            </DashboardPage>
          ),
        },
        {
          path: "connections/new-org-connection",
          element: (
            <DashboardPage>
              <NewOrgConnectionPage />
            </DashboardPage>
          ),
        },
        {
          path: "connections/new-user-connection",
          element: (
            <DashboardPage>
              <NewUserConnectionPage />
            </DashboardPage>
          ),
        },
      ],
    },
  ]);

  return routes;
};

export default AppRoutes;
